import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [allowRegister, setAllowRegister] = useState(false);

  useEffect(() => {
    api.get("/auth/registration-status")
      .then((r) => setAllowRegister(!!r.data.allow_registration))
      .catch(() => setAllowRegister(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Hoş geldin ${u.full_name}!`);
      nav(u.role === "parent" ? "/veli" : "/panel", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Giriş başarısız");
    } finally {
      setBusy(false);
    }
  };

  const quickFill = (e, p) => {
    setEmail(e);
    setPassword(p);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-orange-100/60 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-rose-100/50 blur-3xl" />

      <div className="w-full max-w-md relative z-10 ela-fade-in">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl bg-[#FF6B6B] flex items-center justify-center text-white shadow-sm">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-xl text-slate-900 leading-none">Ela AI Akademi</div>
            <div className="text-xs text-slate-500 mt-1">LGS 2027 Hazırlık Platformu</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Tekrar hoş geldin</h1>
          <p className="text-slate-500 text-sm mt-1">Bismillah, çalışmaya devam.</p>

          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="ela@elaaiakademi.com"
                className="rounded-xl h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-slate-700">Şifre</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
                className="rounded-xl h-11"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-xl bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-medium active:scale-[0.98] transition-transform"
            >
              {busy ? "Giriş yapılıyor…" : "Giriş Yap"}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-100">
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3">
              <Sparkles className="w-3 h-3" /> Hızlı Demo Girişi
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="quick-fill-student"
                onClick={() => quickFill("ela@elaaiakademi.com", "Ela2026!")}
                className="text-xs px-3 py-2 rounded-lg border border-slate-200 hover:border-[#FF6B6B] hover:text-[#FF6B6B] text-slate-600 transition"
              >
                🎓 Öğrenci (Ela)
              </button>
              <button
                type="button"
                data-testid="quick-fill-parent"
                onClick={() => quickFill("veli@elaaiakademi.com", "Veli2026!")}
                className="text-xs px-3 py-2 rounded-lg border border-slate-200 hover:border-[#FF6B6B] hover:text-[#FF6B6B] text-slate-600 transition"
              >
                👨‍👩‍👧 Veli Paneli
              </button>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            {allowRegister ? (
              <>Hesabın yok mu?{" "}
                <Link to="/kayit" className="text-[#FF6B6B] font-medium hover:underline" data-testid="link-to-register">
                  Kayıt ol
                </Link>
              </>
            ) : (
              <span className="text-xs text-slate-400">Bu platform özel kullanıma kapalıdır. Yöneticiniz ile iletişime geçin.</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
